import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  MoreVertical,
  Type,
  Grid3X3,
  ListChecks,
  Quote,
  Image,
  Globe,
  MousePointer,
  FileText,
  BarChart3,
  Layers,
} from "lucide-react";
import { useState } from "react";

interface Props {
  section: any;
  isActive: boolean;
  onClick: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleVisibility: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const sectionIcons: Record<string, any> = {
  "simple-text": Type,
  "feature-grid": Grid3X3,
  "dark-grid": Layers,
  "text-with-points": ListChecks,
  "steps": ListChecks,
  "testimonial-grid": Quote,
  "portfolio-grid": Image,
  "website-showcase": Globe,
  "call-to-action": MousePointer,
  "footer": FileText,
  "stats": BarChart3,
  "hero": Layers,
};

const SortableSection = ({
  section,
  isActive,
  onClick,
  onDelete,
  onDuplicate,
  onToggleVisibility,
  onMoveUp,
  onMoveDown,
}: Props) => {
  const [showMenu, setShowMenu] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = sectionIcons[section.type] || Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative rounded-lg border-2 transition-all ${
        isDragging
          ? "opacity-50 border-emerald-500 bg-emerald-50"
          : isActive
          ? "border-emerald-500 bg-emerald-50"
          : "border-transparent hover:border-gray-200 bg-white hover:bg-gray-50"
      } ${section.hidden ? "opacity-50" : ""}`}
    >
      <div
        onClick={onClick}
        className="flex items-center gap-2 p-3 cursor-pointer"
      >

        <button
          {...attributes}
          {...listeners}
          className="p-1 rounded hover:bg-gray-200 cursor-grab active:cursor-grabbing touch-none"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </button>

        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isActive ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">
            {section.title}
          </p>
          <p className="text-xs text-gray-400 truncate">
            {section.type} • {section.layout}
          </p>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">

          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility();
            }}
            className="p-1.5 rounded hover:bg-gray-200"
            title={section.hidden ? "Show section" : "Hide section"}
          >
            {section.hidden ? (
              <EyeOff className="w-3.5 h-3.5 text-gray-400" />
            ) : (
              <Eye className="w-3.5 h-3.5 text-gray-400" />
            )}
          </button>

          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1.5 rounded hover:bg-gray-200"
            >
              <MoreVertical className="w-3.5 h-3.5 text-gray-400" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveUp();
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <ChevronUp className="w-4 h-4" />
                    Move Up
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveDown();
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <ChevronDown className="w-4 h-4" />
                    Move Down
                  </button>
                  <div className="h-px bg-gray-100 my-1" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate();
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4" />
                    Duplicate
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleVisibility();
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {section.hidden ? (
                      <>
                        <Eye className="w-4 h-4" />
                        Show
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Hide
                      </>
                    )}
                  </button>
                  <div className="h-px bg-gray-100 my-1" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm("Delete this section?")) {
                        onDelete();
                      }
                      setShowMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-emerald-500 rounded-r-full" />
      )}
    </div>
  );
};

export default SortableSection;
